import { supabase, supabaseEnabled } from './supabase';
import { getDB, listBooks, pendingHighlights, getChapters, type LocalBook } from './db';

/**
 * Sync queue. Phone is the producer (books, chapters, highlights, progress).
 * Supabase is the mailbox. The laptop sync worker is the consumer that writes to SiYuan.
 *
 * Phone -> Supabase leg only. Runs:
 *  - on app boot (drainOnce)
 *  - every 30s while online (start())
 *  - on `online` event
 *  - explicitly after upload (pushBookToMailbox)
 */

let timer: number | null = null;

async function getUserId(): Promise<string | null> {
  if (!supabaseEnabled) return null;
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Push a book + all its chapters to Supabase.
 * Called from Upload.tsx after parse, and from drainOnce for offline-uploaded books.
 * Idempotent: upserts by (user, book) keys.
 */
export async function pushBookToMailbox(book: LocalBook) {
  if (!supabaseEnabled || !navigator.onLine) return;
  if (book.kind === 'seed' || book.file_hash === 'seed') return; // local-only
  const userId = await getUserId();
  if (!userId) return;

  try {
    const { error: bookErr } = await supabase.from('books').upsert({
      id: book.id,
      user_id: userId,
      title: book.title,
      author: book.author,
      file_hash: book.file_hash,
      pages: book.pages,
      chapters: book.chapters,
      cover_kind: book.cover_kind,
      metadata: { kind: book.kind, lang: book.lang, blurb: book.blurb ?? null },
      sync_status: 'pending',
    });
    if (bookErr) {
      console.warn('[sync] push book failed', bookErr);
      return;
    }

    const chapters = await getChapters(book.id);
    const BATCH = 20;
    for (let i = 0; i < chapters.length; i += BATCH) {
      const slice = chapters.slice(i, i + BATCH).map((c) => ({
        user_id: userId,
        book_id: book.id,
        idx: c.idx,
        title: c.title,
        text: c.text,
      }));
      const { error: chErr } = await supabase.from('chapters').upsert(slice);
      if (chErr) {
        console.warn('[sync] push chapters failed', chErr);
        return;
      }
    }
    console.log('[sync] pushed book %s with %d chapters', book.title, chapters.length);
  } catch (err) {
    console.warn('[sync] pushBookToMailbox failed', err);
  }
}

/**
 * Ensure all local books exist in Supabase.
 * Catches books uploaded while offline - their initial pushBookToMailbox was a no-op.
 */
async function ensureBooksInMailbox() {
  if (!supabaseEnabled || !navigator.onLine) return;
  const userId = await getUserId();
  if (!userId) return;

  try {
    const localBooks = await listBooks();
    if (!localBooks.length) return;

    // Fetch book ids already in Supabase (single lightweight query)
    const { data: remoteBooks } = await supabase
      .from('books')
      .select('id')
      .eq('user_id', userId);
    const remoteIds = new Set((remoteBooks ?? []).map((b: { id: string }) => b.id));

    for (const book of localBooks) {
      // Seed/sample books never leave the device. Check both kind AND
      // file_hash because the seed data sets file_hash='seed' but doesn't
      // set kind, which leaked past the old check.
      if (book.kind === 'seed' || book.file_hash === 'seed') continue;
      if (!remoteIds.has(book.id)) {
        console.log('[sync] found unpushed book %s, pushing now', book.title);
        await pushBookToMailbox(book);
      }
    }
  } catch (err) {
    console.warn('[sync] ensureBooksInMailbox failed', err);
  }
}

/**
 * Push reading position to Supabase. Debounced caller side (savePosition).
 * Upsert is idempotent: last write wins per (user, book).
 */
export async function pushProgress(bookId: string, ch: number, sent: number, scroll: number) {
  if (!supabaseEnabled || !navigator.onLine) return;
  const userId = await getUserId();
  if (!userId) return;
  try {
    const { error } = await supabase.from('progress').upsert({
      user_id: userId,
      book_id: bookId,
      chapter: ch,
      sentence: sent,
      scroll,
      position: scroll,
      page: Math.max(0, Math.round(scroll * 100)),
      updated_at: new Date().toISOString(),
    });
    if (error) console.warn('[sync] pushProgress', error.message);
  } catch (e) { console.warn('[sync] pushProgress threw', e); }
}

/**
 * Pull every remote progress row for the current user and patch local
 * books in IndexedDB. Called at boot so the library view shows the
 * "30% through chapter 3" bar even after site-data-clears/reinstalls.
 */
export async function pullAllProgress(): Promise<number> {
  if (!supabaseEnabled || !navigator.onLine) return 0;
  const userId = await getUserId();
  if (!userId) return 0;
  try {
    const { data, error } = await supabase
      .from('progress')
      .select('book_id, chapter, sentence, scroll, updated_at')
      .eq('user_id', userId);
    if (error || !data) return 0;
    const { getDB, computeProgressFraction, getChapters } = await import('./db');
    const db = await getDB();
    let updated = 0;
    for (const row of data as any[]) {
      const book = await db.get('books', row.book_id);
      if (!book) continue;
      const ts = Date.parse(row.updated_at);
      const localTs = book.position?.ts ?? 0;
      if (ts <= localTs) continue;
      const scroll = row.scroll ?? 0;
      const ch = row.chapter ?? 0;
      // Backfill chapter_words from local chapters on first sight so the
      // library progress %% is correct even before the Reader opens.
      let chapter_words = book.chapter_words;
      if (!chapter_words || !chapter_words.length) {
        const chs = await getChapters(book.id);
        if (chs.length) chapter_words = chs.map((c) => (c.text || '').split(/\s+/).filter(Boolean).length);
      }
      await db.put('books', {
        ...book,
        chapter_words,
        position: { ch, sent: row.sentence ?? 0, scroll, ts },
        progress: computeProgressFraction(chapter_words, ch, scroll),
      });
      updated++;
    }
    return updated;
  } catch (e) { console.warn('[sync] pullAllProgress threw', e); return 0; }
}

/**
 * Pull reading position from Supabase. Returns null if no remote position
 * or if remote is older than what's already in local IndexedDB.
 */
export async function pullProgress(bookId: string, localTs = 0): Promise<{ ch: number; sent: number; scroll: number; ts: number } | null> {
  if (!supabaseEnabled || !navigator.onLine) return null;
  const userId = await getUserId();
  if (!userId) return null;
  try {
    const { data } = await supabase
      .from('progress')
      .select('chapter, sentence, scroll, updated_at')
      .eq('user_id', userId)
      .eq('book_id', bookId)
      .maybeSingle();
    if (!data) return null;
    const ts = Date.parse((data as any).updated_at);
    if (ts <= localTs) return null;
    return { ch: (data as any).chapter ?? 0, sent: (data as any).sentence ?? 0, scroll: (data as any).scroll ?? 0, ts };
  } catch (e) { console.warn('[sync] pullProgress threw', e); return null; }
}

/**
 * Drain pending highlights + ensure books are in mailbox.
 */
export async function drainOnce() {
  if (!supabaseEnabled || !navigator.onLine) return;
  try {
    const userId = await getUserId();
    if (!userId) return;

    // Ensure offline-uploaded books reach Supabase
    await ensureBooksInMailbox();

    // Push pending highlights
    const pending = await pendingHighlights();
    if (!pending.length) return;

    const db = await getDB();
    for (const h of pending) {
      const { error } = await supabase.from('highlights').upsert({
        id: h.id,
        user_id: userId,
        book_id: h.book_id,
        chapter: h.chapter,
        page: h.page,
        text: h.text,
        note: h.note,
        kind: h.kind,
        status: 'pending',
        created_at: new Date(h.created_at).toISOString(),
      });
      if (!error) {
        await db.put('highlights', { ...h, status: 'synced' });
      }
    }
  } catch (err) {
    console.warn('[sync] drain failed', err);
  }
}

export function start() {
  if (timer) return;
  drainOnce();
  timer = window.setInterval(drainOnce, 30_000);
  window.addEventListener('online', drainOnce);
}

export function stop() {
  if (timer) window.clearInterval(timer);
  timer = null;
  window.removeEventListener('online', drainOnce);
}
